import React, { useState, useEffect, useCallback } from 'react'
import { Building2, RefreshCw, Download } from 'lucide-react'
import { AccountFunders, FunderFilter } from '../types'
import { fundersService } from '../services/fundersService'

interface FunderOverviewTabProps {
  filter?: FunderFilter
}

export const FunderOverviewTab: React.FC<FunderOverviewTabProps> = ({ filter }) => {
  const [accountFunders, setAccountFunders] = useState<AccountFunders[]>([])
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)

  const loadFunderData = useCallback(async () => {
    try {
      setLoading(true)
      const funders = await fundersService.getAccountFunders(filter)
      setAccountFunders(funders)
    } catch (error) {
      console.error('Error loading funder data:', error)
    } finally {
      setLoading(false)
    }
  }, [filter])

  useEffect(() => {
    loadFunderData()
  }, [loadFunderData])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const handleExport = async () => {
    try {
      setExporting(true)
      const csvContent = await fundersService.exportFunders(filter)
      
      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `funders-overview-${new Date().toISOString().split('T')[0]}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    } catch (error) {
      console.error('Error exporting data:', error)
      alert('Failed to export data')
    } finally {
      setExporting(false)
    }
  }

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        height: '200px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            animation: 'spin 1s linear infinite', 
            borderRadius: '50%', 
            height: '32px', 
            width: '32px', 
            border: '2px solid #3b82f6', 
            borderBottomColor: 'transparent',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
            Loading funder overview...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Building2 style={{ height: '20px', width: '20px', color: '#6b7280' }} />
            <h2 style={{ 
              fontSize: '20px', 
              fontWeight: '600', 
              color: '#111827',
              fontFamily: 'Lexend Deca, sans-serif',
              margin: 0
            }}>
              Funder Overview
            </h2>
            <span style={{ 
              fontSize: '14px', 
              color: '#6b7280',
              fontFamily: 'Lexend Deca, sans-serif'
            }}>
              ({accountFunders.reduce((total, account) => total + account.total_funders, 0)} unique funders)
            </span>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleExport}
              disabled={exporting || accountFunders.length === 0}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '8px 16px',
                background: exporting ? '#f3f4f6' : '#10b981',
                color: exporting ? '#6b7280' : 'white',
                fontWeight: '500',
                borderRadius: '8px',
                border: 'none',
                cursor: exporting ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontFamily: 'Lexend Deca, sans-serif',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!exporting && accountFunders.length > 0) {
                  e.currentTarget.style.background = '#059669'
                }
              }}
              onMouseLeave={(e) => {
                if (!exporting && accountFunders.length > 0) {
                  e.currentTarget.style.background = '#10b981'
                }
              }}
            >
              <Download style={{ height: '16px', width: '16px', marginRight: '8px' }} />
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
            <button
              onClick={loadFunderData}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '8px 16px',
                background: 'white',
                color: '#374151',
                fontWeight: '500',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: 'Lexend Deca, sans-serif',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
            >
              <RefreshCw style={{ height: '16px', width: '16px', marginRight: '8px' }} />
              Refresh
            </button>
          </div>
        </div>

        {/* Account Funders */}
        {accountFunders.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '64px',
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #e5e7eb'
          }}>
            <Building2 style={{ 
              height: '48px', 
              width: '48px', 
              color: '#d1d5db', 
              margin: '0 auto 16px' 
            }} />
            <h3 style={{ 
              fontSize: '18px', 
              fontWeight: '500', 
              color: '#6b7280',
              fontFamily: 'Lexend Deca, sans-serif',
              marginBottom: '8px'
            }}>
              No funders found
            </h3>
            <p style={{ 
              color: '#9ca3af',
              fontFamily: 'Lexend Deca, sans-serif'
            }}>
              No companies with "Funder" association found in the current dataset
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {accountFunders.map((accountGroup) => (
              <div key={accountGroup.account} style={{ 
                background: 'white', 
                borderRadius: '12px', 
                overflow: 'hidden', 
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb'
              }}>
                {/* Account Header */}
                <div style={{
                  padding: '20px',
                  background: '#f9fafb',
                  borderBottom: '1px solid #e5e7eb'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h3 style={{ 
                      fontSize: '18px', 
                      fontWeight: '600', 
                      color: '#111827',
                      fontFamily: 'Lexend Deca, sans-serif',
                      margin: 0
                    }}>
                      {accountGroup.account}
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ 
                          fontSize: '16px', 
                          fontWeight: '600', 
                          color: '#10b981',
                          fontFamily: 'Lexend Deca, sans-serif'
                        }}>
                          {formatCurrency(accountGroup.total_amount)}
                        </div>
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#6b7280',
                          fontFamily: 'Lexend Deca, sans-serif'
                        }}>
                          Total Amount
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ 
                          fontSize: '16px', 
                          fontWeight: '600', 
                          color: '#3b82f6',
                          fontFamily: 'Lexend Deca, sans-serif'
                        }}>
                          {accountGroup.total_deals}
                        </div>
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#6b7280',
                          fontFamily: 'Lexend Deca, sans-serif'
                        }}>
                          Total Deals
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ 
                          fontSize: '16px', 
                          fontWeight: '600', 
                          color: '#8b5cf6',
                          fontFamily: 'Lexend Deca, sans-serif'
                        }}>
                          {accountGroup.total_funders}
                        </div>
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#6b7280',
                          fontFamily: 'Lexend Deca, sans-serif'
                        }}>
                          Unique Funders
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Funders List */}
                <div style={{ padding: '0' }}>
                  {accountGroup.funders.map((funder, index) => (
                    <div 
                      key={funder.company_id} 
                      style={{
                        padding: '16px 20px',
                        borderBottom: index < accountGroup.funders.length - 1 ? '1px solid #f3f4f6' : 'none'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span style={{ 
                              fontSize: '16px', 
                              fontWeight: '500', 
                              color: funder.company_id === 'no-funder-defined' ? '#dc2626' : '#111827',
                              fontFamily: 'Lexend Deca, sans-serif',
                              fontStyle: funder.company_id === 'no-funder-defined' ? 'italic' : 'normal'
                            }}>
                              {funder.company_name}
                            </span>
                            {funder.company_domain && (
                              <span style={{ 
                                fontSize: '14px', 
                                color: '#6b7280',
                                fontFamily: 'Lexend Deca, sans-serif'
                              }}>
                                {funder.company_domain}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ 
                              fontSize: '16px', 
                              fontWeight: '600', 
                              color: '#10b981',
                              fontFamily: 'Lexend Deca, sans-serif'
                            }}>
                              {formatCurrency(funder.total_amount)}
                            </div>
                            <div style={{ 
                              fontSize: '12px', 
                              color: '#6b7280',
                              fontFamily: 'Lexend Deca, sans-serif'
                            }}>
                              Total Amount
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', minWidth: '80px' }}>
                            <div style={{ 
                              fontSize: '16px', 
                              fontWeight: '600', 
                              color: '#3b82f6',
                              fontFamily: 'Lexend Deca, sans-serif'
                            }}>
                              {funder.deal_count}
                            </div>
                            <div style={{ 
                              fontSize: '12px', 
                              color: '#6b7280',
                              fontFamily: 'Lexend Deca, sans-serif'
                            }}>
                              {funder.deal_count === 1 ? 'Deal' : 'Deals'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}